'use client';

import Link from 'next/link';
import Slider from 'react-slick';
import { twMerge } from 'tailwind-merge';

import { defaultDataTagGroup } from '@/constants/defaultSeo';
import { IComponentHomeHero } from '@/types/cms';
import { faArrowLeft, faArrowRight, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CommonImage } from '../CommonImage';

interface ICustomArrowProps {
  isRight?: boolean;
  onClick?: () => void;
}

const CustomArrow = ({ isRight, onClick }: ICustomArrowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer z-10 outline-none size-9 md:size-7 lg:size-10 md:rounded-full text-white max-md:hover:text-gray-100 md:text-black-500 md:bg-white md:hover:bg-gray-100',
      isRight ? 'right-0 md:right-4 lg:right-8' : 'left-0 md:left-4 lg:left-8',
    )}
  >
    <FontAwesomeIcon
      className="size-6 drop-shadow-[0px_0px_3px_0px_#00000080] md:hidden"
      icon={isRight ? faChevronRight : faChevronLeft}
    />
    <FontAwesomeIcon className="size-3 lg:size-4 max-md:hidden" icon={isRight ? faArrowRight : faArrowLeft} />
  </button>
);

const CustomPaging = () => (
  <div className="size-3 md:size-2.5 lg:size-3.5 rounded-full border border-white p-0.5 cursor-pointer hover:border-gray-100">
    <figure className="size-full rounded-full" />
  </div>
);

interface IHeroOverlayProps {
  link_target_blank?: boolean;
  overlay_text?: string;
  overlay_subtext?: string;
  overlay_cta_text?: string;
  overlay_cta_link?: string;
  classes?: string;
  classes_overlay_text?: string;
  classes_overlay_subtext?: string;
  classes_overlay_cta?: string;
  classes_extra?: string;
}

const HeroOverlay = (props: IHeroOverlayProps) => {
  if (!props.overlay_text && !props.overlay_subtext && !props.overlay_cta_text && !props.overlay_cta_link) return null;

  return (
    <div
      className={twMerge(
        'absolute bottom-0 left-0 size-full flex flex-col justify-center items-center text-white font-helvetica',
        props.classes,
        props.classes_extra,
      )}
    >
      {!!props.overlay_text && (
        <h2 className={twMerge('text-3xl font-bold md:text-5xl text-white', props.classes_overlay_text)}>
          {props.overlay_text}
        </h2>
      )}
      {!!props.overlay_subtext && (
        <p className={twMerge('mt-4 md:mt-2.5 text-sm md:text-base text-white', props.classes_overlay_subtext)}>
          {props.overlay_subtext}
        </p>
      )}
      {!!props.overlay_cta_text && !!props.overlay_cta_link && (
        <Link
          href={props.overlay_cta_link || '#'}
          className={twMerge(
            'mt-6 md:mt-5 px-8 md:px-9 py-2.5 md:py-3 rounded-md flex items-center justify-center text-sm md:text-base font-bold bg-white text-[#21201F] hover:bg-[#F5F5F5] hover:text-[#21201F]',
            props.classes_overlay_cta,
          )}
          target={props.link_target_blank ? '_blank' : '_self'}
        >
          {props.overlay_cta_text}
        </Link>
      )}
    </div>
  );
};

type HeroImageType = IComponentHomeHero['images'][number];

const HeroImage = (image: HeroImageType) => {
  const Container = image.overlay_link ? Link : 'div';

  return (
    <Container
      href={image.overlay_link || undefined}
      className="relative overflow-hidden"
      target={image.link_target_blank ? '_blank' : '_self'}
    >
      <CommonImage
        src={image.mobile_url}
        alt={image.alt}
        className="rounded-xl md:rounded lg:rounded-[0.625rem] size-full object-cover min-[430px]:hidden"
      />
      <CommonImage
        src={image.large_mobile_url}
        alt={image.alt}
        className="rounded-xl md:rounded lg:rounded-[0.625rem] size-full object-cover max-[429px]:hidden md:hidden"
      />
      <CommonImage
        src={image.tablet_url}
        alt={image.alt}
        className="rounded-xl md:rounded lg:rounded-[0.625rem] size-full object-cover max-md:hidden lg:hidden"
      />
      <CommonImage
        src={image.small_desktop_url}
        alt={image.alt}
        className="rounded-xl md:rounded lg:rounded-[0.625rem] size-full object-cover max-lg:hidden min-[1440px]:hidden"
      />
      <CommonImage
        src={image.medium_desktop_url}
        alt={image.alt}
        className="rounded-xl md:rounded lg:rounded-[0.625rem] size-full object-cover max-[1439px]:hidden min-[1920px]:hidden"
      />
      <CommonImage
        src={image.large_desktop_url}
        alt={image.alt}
        className="rounded-xl md:rounded lg:rounded-[0.625rem] size-full object-cover max-[1919px]:hidden"
      />
      <HeroOverlay
        link_target_blank={image.link_target_blank}
        overlay_text={image.mobile_overlay_text}
        overlay_subtext={image.mobile_overlay_subtext}
        overlay_cta_text={image.mobile_overlay_cta_text}
        overlay_cta_link={image.mobile_overlay_cta_link}
        classes={image.classes_overlay}
        classes_overlay_text={image.classes_overlay_text}
        classes_overlay_subtext={image.classes_overlay_subtext}
        classes_overlay_cta={image.classes_overlay_cta}
        classes_extra="md:hidden"
      />
      <HeroOverlay
        link_target_blank={image.link_target_blank}
        overlay_text={image.tablet_overlay_text}
        overlay_subtext={image.tablet_overlay_subtext}
        overlay_cta_text={image.tablet_overlay_cta_text}
        overlay_cta_link={image.tablet_overlay_cta_link}
        classes={image.classes_overlay}
        classes_overlay_text={image.classes_overlay_text}
        classes_overlay_subtext={image.classes_overlay_subtext}
        classes_overlay_cta={image.classes_overlay_cta}
        classes_extra="max-md:hidden lg:hidden"
      />
      <HeroOverlay
        link_target_blank={image.link_target_blank}
        overlay_text={image.desktop_overlay_text}
        overlay_subtext={image.desktop_overlay_subtext}
        overlay_cta_text={image.desktop_overlay_cta_text}
        overlay_cta_link={image.desktop_overlay_cta_link}
        classes={image.classes_overlay}
        classes_overlay_text={image.classes_overlay_text}
        classes_overlay_subtext={image.classes_overlay_subtext}
        classes_overlay_cta={image.classes_overlay_cta}
        classes_extra="max-lg:hidden"
      />
    </Container>
  );
};

export const HomeHero = ({
  classes,
  data_tag,
  data_tag_group,
  disable_pause_on_hover,
  display_arrows,
  display_dots,
  images,
  speed,
}: IComponentHomeHero) => {
  const autoplaySpeed = speed ? speed * 1000 : undefined;

  const displayArrows =
    display_arrows === undefined || display_arrows === 'default' ? images.length > 1 : display_arrows === 'true';
  const displayDots =
    display_dots === undefined || display_dots === 'default' ? images.length > 1 : display_dots === 'true';

  return (
    <div
      className={twMerge('px-6 md:px-10 my-8 md:my-10 lg:my-16 lg:container lg:mx-auto', classes)}
      data-tag={data_tag || 'homeHero'}
      data-tag-group={data_tag_group || defaultDataTagGroup}
    >
      <Slider
        slidesToScroll={1}
        slidesToShow={1}
        infinite={images.length > 1}
        arrows={displayArrows}
        nextArrow={<CustomArrow isRight />}
        prevArrow={<CustomArrow />}
        dots={displayDots}
        pauseOnHover={disable_pause_on_hover ? false : true}
        dotsClass="absolute bottom-5 md:bottom-3 lg:bottom-5 right-1/2 !flex gap-1.5 translate-x-1/2 [&>li.slick-active_figure]:!bg-white z-10"
        customPaging={CustomPaging}
        adaptiveHeight
        autoplay
        autoplaySpeed={autoplaySpeed || 3000}
      >
        {images.map((image, index) => {
          return <HeroImage {...image} key={image.alt + index} />;
        })}
      </Slider>
    </div>
  );
};
